/**
 * La bibliothèque vivante — **remaster** et **séries** (SPEC-MOTEUR §6.3, §6.5, PLAN §4.6).
 *
 * ── Ce que ce module répare ────────────────────────────────────────────────────
 *
 * Deux actions **validées, facturées, et sans le moindre effet** :
 *
 * - **`remaster`** — exigeait une evergreen, prélevait 16 h + 27 ⚡ + 1 carte, puis tombait
 *   dans le `default:` de la file. `evergreen.anchor_h` n'était écrit qu'à la publication :
 *   *la demi-vie n'était jamais réarmée*, ce qui est très exactement la seule chose que
 *   l'action promet (§6.3 : « Remaster : `anchor_h := clock_h`, `weekly_rate` recalculé »).
 * - **`ranger_serie`** — exigeait ≥ 3 vidéos, prélevait 2 h + 4 ⚡, puis `default:`.
 *   `state.series` était initialisé `[]` et **jamais réécrit**, `VideoRecord.series_id` figé à
 *   `null`. Conséquence en chaîne : **`RENTE_MULT_SERIE = 1,2` était du code mort**, le
 *   « +20 % de rente croisée » de PLAN §4.6 ne s'appliquait jamais, et le `state.series.find`
 *   de l'écran Bibliothèque ne trouvait rien — ce qui vidait cet écran de sa raison d'être.
 *
 * ── Ce que le ×1,2 exige, et qui n'était nulle part ───────────────────────────
 *
 * `tauxRenteFige` **fige** le taux de rente à la publication (§6.3). Une vidéo rangée en série
 * APRÈS coup ne peut donc pas en profiter… à moins qu'on ne recalcule son taux au moment du
 * rangement. C'est ce que fait `recalculerRentesDeSerie`, et c'est aussi ce qui rend le
 * `tauxRenteFige(mods, false)` de `publish.ts` **correct** : une vidéo qui vient de naître
 * n'est jamais déjà dans une série. Le second argument n'était pas « en dur à false » par
 * erreur — il était en dur parce qu'aucune série ne pouvait exister.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * Aucune série, aucun remaster ⇒ le moteur se comporte exactement comme avant : `series` reste
 * `[]`, `series_id` reste `null`, et `tauxRenteFige(mods, false)` rend la base. Aucune ancre ne
 * bouge (elles n'appellent que `computeVideoOutcome`, qui ignore la bibliothèque).
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`.
 */
import { collectModifiers, hasCapability } from "./effects.js";
import { RENTE_MULT_SERIE, tauxRenteFige } from "./publish.js";
import { InvalidAction } from "./types.js";
/** Nombre minimal de vidéos pour former une série (§6.5, PLAN §4.6). */
export const SERIE_TAILLE_MIN = 3;
// ─────────────────────────────────────────────────────────────────────────────
// Lectures
// ─────────────────────────────────────────────────────────────────────────────
/** La série dont cette vidéo est membre, s'il y en a une. */
export function serieDeLaVideo(state, videoId) {
    return state.series.find((s) => s.video_ids.includes(videoId));
}
/** Cette vidéo est-elle en série ? C'est le booléen que `tauxRenteFige` attend. */
export function enSerie(state, video) {
    return video.series_id !== null;
}
/** Thème du sujet d'une vidéo — `null` si le contenu ne le connaît pas. */
function themeDeLaVideo(content, video) {
    return content.subjects.find((s) => s.id === video.subject_id)?.theme ?? null;
}
/**
 * ✎ Les vidéos d'une série doivent être **compatibles** — §6.5 dit « même thème ✎ ».
 *
 * Une vidéo dont le sujet a disparu du contenu est traitée comme incompatible plutôt
 * qu'ignorée : mieux vaut refuser une série qu'en former une dont on ne sait rien dire.
 */
export function memeTheme(content, videos) {
    const themes = new Set(videos.map((v) => themeDeLaVideo(content, v)));
    return themes.size === 1 && !themes.has(null);
}
// ─────────────────────────────────────────────────────────────────────────────
// Validation (§2.2) — lève sans rien consommer
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦C13⟧ Les deux capacités de bibliothèque étaient vérifiées **côté client seulement**
 * (`Bibliotheque.tsx`) : un appel direct à l'API contournait l'arbre de skills. Le moteur est
 * autoritatif (PLAN §6), donc c'est ici que ça se refuse.
 */
export function exigerCapacite(state, content, capacite) {
    const mods = collectModifiers(state, {
        skills: (content.skills ?? []),
        boutique: (content.boutique ?? []),
    });
    if (!hasCapability(mods, capacite)) {
        throw new InvalidAction("mecanique_verrouillee", { mecanique: capacite });
    }
}
/** Validation de `ranger_serie` (§6.5). Rend les vidéos concernées. */
export function validateRangerSerie(state, videoIds, content) {
    exigerCapacite(state, content, "series_bibliotheque");
    const ids = [...new Set(videoIds)];
    if (ids.length < SERIE_TAILLE_MIN) {
        throw new InvalidAction("series_too_small", { fournies: ids.length, requis: SERIE_TAILLE_MIN });
    }
    const videos = [];
    for (const id of ids) {
        const v = state.videos.find((x) => x.id === id);
        if (!v)
            throw new InvalidAction("video_not_found", { video_id: id });
        if (v.series_id !== null) {
            throw new InvalidAction("video_wrong_class", { video_id: id, raison: "deja_en_serie" });
        }
        videos.push(v);
    }
    if (!memeTheme(content, videos)) {
        throw new InvalidAction("video_wrong_class", { raison: "themes_melanges" });
    }
    return videos;
}
// ─────────────────────────────────────────────────────────────────────────────
// Les deux effets
// ─────────────────────────────────────────────────────────────────────────────
/**
 * **Remaster** (§6.3) : la demi-vie repart de zéro et le taux de rente est recalculé sur les
 * modificateurs d'AUJOURD'HUI — c'est ce qui permet de rattraper un SEO acheté après coup, ou
 * une mise en série. MUTE `state`.
 */
export function appliquerRemaster(state, videoId, content, events = [], log = []) {
    const video = state.videos.find((v) => v.id === videoId);
    // La validation (§2.2) l'a déjà écarté ; garde-fou pour ne jamais remaster à l'aveugle.
    if (!video || !video.evergreen)
        throw new InvalidAction("video_wrong_class", { video_id: videoId });
    const mods = collectModifiers(state, {
        skills: (content.skills ?? []),
        boutique: (content.boutique ?? []),
    });
    const avant = { anchor_h: video.evergreen.anchor_h, weekly_rate: video.evergreen.weekly_rate };
    video.evergreen.anchor_h = state.clock_h;
    video.evergreen.weekly_rate = tauxRenteFige(mods, enSerie(state, video));
    events.push({
        kind: "event_resolved",
        data: {
            cause: "remaster",
            video_id: video.id,
            anchor_avant_h: avant.anchor_h,
            anchor_apres_h: video.evergreen.anchor_h,
            taux_avant: avant.weekly_rate,
            taux_apres: video.evergreen.weekly_rate,
        },
    });
    log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        famille: "chaine",
        text: "Nouvelle miniature, nouveau titre, description refaite : la vidéo repart de zéro.",
    });
    return video;
}
/**
 * Recalcule le taux de rente des membres **evergreen** d'une série — sans ça, le ×1,2 de
 * PLAN §4.6 n'atteindrait jamais le versement, puisque `tauxRenteFige` fige à la publication
 * et qu'une vidéo est rangée en série APRÈS. MUTE `state`. Rend le nombre de taux relevés.
 */
export function recalculerRentesDeSerie(state, videos, content) {
    const mods = collectModifiers(state, {
        skills: (content.skills ?? []),
        boutique: (content.boutique ?? []),
    });
    let releves = 0;
    for (const v of videos) {
        if (!v.evergreen)
            continue;
        const avant = v.evergreen.weekly_rate;
        v.evergreen.weekly_rate = tauxRenteFige(mods, true);
        if (v.evergreen.weekly_rate !== avant)
            releves += 1;
    }
    return releves;
}
/** **Ranger en série** (§6.5). MUTE `state`. */
export function appliquerRangerSerie(state, videoIds, nom, content, events = [], log = []) {
    const videos = validateRangerSerie(state, videoIds, content);
    // Id stable et déterministe : la position dans la liste, comme `VideoRecord.id`. Un id
    // dérivé du nom se casserait au premier doublon, et un id aléatoire romprait la pureté.
    const serie = {
        id: `serie_${state.series.length + 1}`,
        name: nom,
        video_ids: videos.map((v) => v.id),
    };
    state.series.push(serie);
    for (const v of videos)
        v.series_id = serie.id;
    const releves = recalculerRentesDeSerie(state, videos, content);
    events.push({
        kind: "event_resolved",
        data: {
            cause: "serie_rangee",
            serie_id: serie.id,
            nom: serie.name,
            videos: serie.video_ids.length,
            rentes_relevees: releves,
            mult_rente: RENTE_MULT_SERIE,
        },
    });
    log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        famille: "chaine",
        text: releves > 0
            ? `« ${nom} » : ${serie.video_ids.length} vidéos rangées, ${releves} rente${releves > 1 ? "s" : ""} relevée${releves > 1 ? "s" : ""} de ${Math.round((RENTE_MULT_SERIE - 1) * 100)} %.`
            : `« ${nom} » : ${serie.video_ids.length} vidéos rangées. La rente croisée jouera dès qu'une d'elles sera evergreen.`,
    });
    return serie;
}
// ─────────────────────────────────────────────────────────────────────────────
// Le hook de résolution (§2.3 pas 6)
// ─────────────────────────────────────────────────────────────────────────────
/** Hook `effetAction` pour `remaster` et `ranger_serie`. Rend `true` si l'action est traitée. */
export function effetActionBibliotheque(ctx, action) {
    if (action.type === "remaster") {
        appliquerRemaster(ctx.state, action.video_id, ctx.content, ctx.events, ctx.log);
        return true;
    }
    if (action.type === "ranger_serie") {
        appliquerRangerSerie(ctx.state, action.video_ids, action.series_name, ctx.content, ctx.events, ctx.log);
        return true;
    }
    return false;
}
//# sourceMappingURL=bibliotheque.js.map